/**
 * 基于Howler.js的无缝音频播放器
 * 专门解决移动端音频播放暂停问题
 */

import { Howl, Howler } from "howler";
import {
  shouldBlockHowlerInAppleHidden,
  logAppleGuard
} from "./applePlaybackGuards";
import { emitter } from "@/utils/emitter/mitt";
import { getConfig } from "@/config";
import { transformI18n, $t } from "@/plugins/i18n";

// 音频工具函数
export function createSilentAudio(): AudioBuffer {
  const audioContext = new (window.AudioContext ||
    (window as any).webkitAudioContext)();
  const buffer = audioContext.createBuffer(1, 1, 22050);
  return buffer;
}

export interface HowlerAudioItem {
  id: string;
  text: string;
  audioUrl: string;
  howl?: Howl;
  duration?: number;
  voiceNo?: number;
  productId?: string | null;
  commentId?: string | null;
}

export class HowlerSeamlessPlayer {
  private playlist: HowlerAudioItem[] = [];
  private currentIndex = 0;
  private isPlaying = false;
  private currentHowl: Howl | null = null;
  private onProgress?: (index: number, progress: number) => void;
  private onComplete?: (index: number) => void;
  private progressTimer?: number; // 不再用于轮询，仅保留占位
  private mobileTimeoutTimer?: number; // 🔧 新增：移动端超时检查定时器
  // 事件驱动的进度监听绑定
  private progressNode?: HTMLAudioElement | null;
  private progressHandlers?: { onTime: any } | null;
  private roomId: string; // 🔧 新增：房间ID，用于调试
  private instanceVolume = 1.0; // 🔧 新增：实例级音量控制
  private instanceMuted = false; // 🔧 新增：实例级静音控制
  // 🔧 新增：防止自动播放的标志，等待进度恢复完成
  private waitingForProgressRestore = false;
  // 🔧 新增：进度恢复开始时间，用于超时检测
  private progressRestoreStartTime = 0;
  // 🔧 新增：播放序列计数器，用于调试
  private playSeqCounter = 0;
  // 🔧 新增：移动端调试日志存储
  private mobileDebugLogs: {
    timestamp: string;
    message: string;
    type: string;
  }[] = [];
  private maxDebugLogs = 100; // 增加到100条日志
  // 错误熔断
  private errorBurstCount = 0;
  private lastErrorAt = 0;

  // 🎵 无缝播放核心状态管理
  private hasTriggeredCompletion = false; // 防止重复触发完成回调
  private isOverlapPlaying = false; // 是否正在重叠播放
  private seamlessProgressTimer?: number; // 无缝播放进度监控定时器
  private static unlocked = false; // 全局音频是否已解锁
  private wasPlayingBeforeHidden = false; // 前后台切换时记录状态
  private initialFadeInDone = false; // 首次自动播放渐入
  private visibilityHandler?: () => void; // 可见性监听器引用，便于清理
  private lastPlayNextTime = 0; // 记录最后一次调用 playNext 的时间
  // 🔒 新增：playNext 单次触发互斥锁，避免后台并发触发
  private _playNextLock = false;
  private appleHiddenFreeze = false; // 🍎 后台冻结：隐藏态下不由 Howler 继续播下一条
  // 🍎 前台切换抑制：在可见性恢复后的短时间内，关闭“前台强制完成检查”，避免与 handoff 冲突
  private suppressForcedCompleteUntil = 0;
  // 🔁 重播保护：记录最近一次启动的音频ID与时间，避免同一段在短时间内被 playNext 二次启动
  private lastStartedItemId: string | null = null;
  private lastStartedAt: number = 0;

  constructor(roomId?: string) {
    this.roomId = roomId || "unknown";
    this.initHowler();
  }

  private detachSeamlessListeners(node: HTMLAudioElement) {
    const existing = (node as any).__seamlessBound as
      | {
          onTime: () => void;
          onEnded: () => void;
          onStalled?: () => void;
          onWaiting?: () => void;
        }
      | undefined;
    if (!existing) return;

    try {
      node.removeEventListener("timeupdate", existing.onTime);
      node.removeEventListener("ended", existing.onEnded);
      if (existing.onStalled)
        node.removeEventListener("stalled", existing.onStalled);
      if (existing.onWaiting)
        node.removeEventListener("waiting", existing.onWaiting);
    } catch {}

    (node as any).__seamlessBound = undefined;
  }

  private detachCoreEndedListener(node: HTMLAudioElement) {
    const existing = (node as any).__coreBound as
      | {
          onEnded: () => void;
        }
      | undefined;
    if (!existing) return;

    try {
      node.removeEventListener("ended", existing.onEnded);
    } catch {}

    (node as any).__coreBound = undefined;
  }

  // 将紧急项安排为“下一条”（当前曲目结束后首先播放）
  async addUrgentNext(item: HowlerAudioItem) {
    const howl = new Howl({
      src: [item.audioUrl],
      html5: this.shouldUseWebAudio() ? false : true,
      preload: true,
      format: ["mp3", "wav", "aac"],
      autoplay: false,
      loop: false,
      ...(this.isMobileDevice() && { buffer: true, pool: 1 }),
      onload: () => {
        try {
          item.duration = howl.duration();
        } catch {}
      },
      onplay: () => {
        // 绑定无缝监听，行为与 addToPlaylist 保持一致
        this.setupSeamlessOverlapPlay(howl, item);
      },
      onend: () => {
        if (!this.hasTriggeredCompletion) {
          this.hasTriggeredCompletion = true;
          this.handleManualAudioComplete();
        }
      },
      onloaderror: () => {
        try {
          howl.unload();
        } catch {}
      },
      onplayerror: () => {
        try {
          howl.unload();
        } catch {}
      }
    });
    item.howl = howl;

    // 实例静音/音量继承
    try {
      howl.volume(this.instanceMuted ? 0 : this.instanceVolume);
    } catch {}

    // 插到当前之后第一位（LIFO）
    const insertPos = Math.min(
      this.playlist.length,
      Math.max(0, this.currentIndex + 1)
    );
    this.playlist.splice(insertPos, 0, item);

    // 若当前未在播（或播放列表空闲），立即启动
    try {
      this.startIfIdle && this.startIfIdle();
    } catch {}
  }

  // 全局解锁：在用户手势下调用，解锁 WebAudio/HTML5 音频
  static async unlock(): Promise<boolean> {
    try {
      const anyHowler: any = Howler as any;
      const ctx = anyHowler.ctx || anyHowler._audioContext;
      if (ctx && ctx.state !== "running" && ctx.resume) {
        await ctx.resume();
      }
      // 额外播放一次极短的静音以确保解锁
      const AC =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AC) {
        const ac = ctx || new AC();
        const buffer = ac.createBuffer(1, 1, 22050);
        const source = ac.createBufferSource();
        source.buffer = buffer;
        source.connect(ac.destination);
        if (source.start) source.start(0);
      }
      HowlerSeamlessPlayer.unlocked = true;
      console.log("🔓 音频通道已解锁");
      return true;
    } catch (e) {
      console.warn("🔒 解锁音频失败:", e);
      return false;
    }
  }

  static isUnlocked(): boolean {
    return HowlerSeamlessPlayer.unlocked;
  }

  // 🎵 无缝播放核心方法：为音频设置无缝播放监听器
  private setupSeamlessOverlapPlay(howl: Howl, item: HowlerAudioItem) {
    // 重置状态标志
    this.hasTriggeredCompletion = false;
    this.isOverlapPlaying = false;

    this.logMobile(
      `为音频设置无缝播放监听："${item.text.substring(0, 30)}..."`,
      "info"
    );

    // 停止之前的监控
    if (this.seamlessProgressTimer) {
      clearInterval(this.seamlessProgressTimer);
      this.seamlessProgressTimer = undefined;
    }

    // 采用底层 HTMLAudioElement 的事件作为唯一推进信号（无自设定时器）
    let audibleSignaled = false;
    const tick = () => {
      // 仅用于触发 onTime 逻辑，无需固定间隔
      if (!howl || !howl.playing() || !item.duration) return;
    };
    try {
      const node = (howl as any)?._sounds?.[0]?._node as
        | HTMLAudioElement
        | undefined;
      if (node) {
        this.detachSeamlessListeners(node);

        const onTime = () => {
          try {
            if (
              !audibleSignaled &&
              howl &&
              typeof howl.playing === "function" &&
              howl.playing()
            ) {
              audibleSignaled = true;
              // 通知可见交接：Howler 已可听
              try {
                emitter.emit("apple:howler-audible", { roomId: this.roomId });
              } catch {}
              try {
                emitter.emit("productAudioStarted", {
                  roomId: this.roomId,
                  productId: (item as any)?.productId ?? null,
                  audioId: item.id,
                  source: "howler"
                });
              } catch {}
            }
          } catch {}
          tick();
        };
        const onEnded = () => {
          // 某些浏览器在还未真正开始播放时也会抛出 ended，这种情况直接忽略
          try {
            const seek =
              typeof howl.seek === "function" ? (howl.seek() as number) : 0;
            const state =
              typeof howl.state === "function" ? howl.state() : "loaded";
            const justStarted =
              this.lastStartedItemId === item.id &&
              Date.now() - this.lastStartedAt < 1000;
            const notReady = state !== "loaded";
            if (
              notReady ||
              (!howl.playing() && (seek === 0 || Number.isNaN(seek))) ||
              (justStarted && seek <= 0.05)
            ) {
              this.logMobile("忽略尚未开始播放时收到的 ended 事件", "info", {
                event: "preplay_ended_ignored",
                seek,
                state,
                justStarted
              });
              return;
            }
          } catch {}

          if (!this.hasTriggeredCompletion) {
            this.logMobile("HTMLAudioElement ended 触发：兜底完成", "warn");
            this.hasTriggeredCompletion = true;
            this.handleManualAudioComplete();
            // 🔧 结束后立即清空当前 howler 引用，避免下一次 playNext 误判“已有当前音频”
            try {
              this.currentHowl = null;
            } catch {}
          }
          // 🍎 隐藏态冻结：在后台真正 ended 时，通知苹果 WA 分支可以起播下一条
          try {
            if (
              this.appleHiddenFreeze &&
              this.isSafari() &&
              typeof document !== "undefined" &&
              document.hidden
            ) {
              emitter.emit("apple:hidden-howler-ended", {
                roomId: this.roomId
              });
            }
          } catch {}
        };
        const onStalled = () =>
          this.logMobile("音频 stalled，等待恢复", "warn");
        const onWaiting = () => this.logMobile("音频 waiting，缓冲中", "info");

        node.addEventListener("timeupdate", onTime);
        node.addEventListener("ended", onEnded);
        node.addEventListener("stalled", onStalled);
        node.addEventListener("waiting", onWaiting);
        (node as any).__seamlessBound = {
          onTime,
          onEnded,
          onStalled,
          onWaiting
        };
      }
    } catch {}
  }

  // 🎵 手动处理音频完成回调（替代onend事件）
  private handleManualAudioComplete() {
    this.logMobile(
      `手动完成回调：索引=${this.currentIndex}/${this.playlist.length}`,
      "warn"
    );

    // 停止无缝监控
    if (this.seamlessProgressTimer) {
      clearInterval(this.seamlessProgressTimer);
      this.seamlessProgressTimer = undefined;
    }

    // 🍎 Safari专用：确保播放状态同步
    if (this.isSafari()) {
      this.isPlaying = false;
      this.logMobile(`🍎 Safari完成回调: 播放状态设为false`, "info");
    }

    if (this.onComplete) {
      this.onComplete(this.currentIndex);
    }

    const finishedIndex = this.currentIndex;
    this.currentIndex++;
    this.logMobile(
      `索引推进到: ${this.currentIndex}, 剩余: ${this.playlist.length - this.currentIndex}`
    );

    // 🔧 释放已播放的 Howl，回收 HTML5 audio 池，降低复用锁定概率
    try {
      const finished = this.playlist[finishedIndex];
      finished?.howl && finished.howl.unload();
    } catch {}

    // 检查是否还有下一个
    if (this.currentIndex >= this.playlist.length) {
      this.logMobile("播放列表完成，停止播放", "warn");
      this.isPlaying = false;
      this.stopProgressTimer();

      // 🍎 Safari专用：更新Media Session状态
      if (this.isSafari() && "mediaSession" in navigator) {
        try {
          (navigator as any).mediaSession.playbackState = "paused";
        } catch {}
      }
      return;
    }

    // 🔧 关键修复：重叠播放时也要确保索引推进和状态同步
    if (!this.isOverlapPlaying) {
      // 🍎 隐藏态冻结：完成后不继续下一条
      if (
        this.appleHiddenFreeze &&
        this.isSafari() &&
        typeof document !== "undefined" &&
        document.hidden
      ) {
        this.logMobile("🍎 隐藏态冻结：完成后不触发 playNext", "warn");
        return;
      }
      this.logMobile('没有重叠播放，手动播放下一个 {"cause":"manual"}', "warn");
      this.playNext("manual");
      // 无固定看门狗：完全依赖 ended/时间进度事件推进
    } else {
      this.logMobile("重叠播放进行中，确保状态同步", "info");
      // 🔧 重叠播放时确保状态正确，避免播放中断
      this.isPlaying = true;
      // 重叠播放的切换由performSoftSwitchToNext处理
    }
  }

  // 🎵 开始重叠播放下一个音频
  private startSeamlessOverlapPlay() {
    // 🍎 Apple hidden guard: avoid starting a new HTML5/Howler play() in background
    try {
      const hidden = typeof document !== "undefined" ? document.hidden : false;
      const hasGuard =
        typeof (globalThis as any).shouldBlockHowlerInAppleHidden ===
        "function";
      const guardDecision = hasGuard
        ? (globalThis as any).shouldBlockHowlerInAppleHidden()
        : "absent";
      if (hidden) {
        this.logMobile(
          `[probe] HOWLER_SEAMLESS_OVERLAP_REQUEST hidden=${hidden} guard=${String(guardDecision)} freeze=${this.appleHiddenFreeze}`,
          "warn"
        );
      }
    } catch {}
    if (
      typeof shouldBlockHowlerInAppleHidden === "function" &&
      shouldBlockHowlerInAppleHidden()
    ) {
      this.logMobile(
        "🍎 iOS hidden: skip Howler overlap play; WebAudio handles scheduling",
        "warn"
      );
      logAppleGuard("skip overlap play() in hidden state");
      return;
    }
    const nextIndex = this.currentIndex + 1;
    if (nextIndex >= this.playlist.length) {
      this.logMobile("没有下一个音频，跳过重叠播放", "info");
      return;
    }

    const nextItem = this.playlist[nextIndex];
    if (!nextItem || !nextItem.howl) {
      this.logMobile("下一个音频项目无效，跳过重叠播放", "warn");
      return;
    }

    this.logMobile(
      `重叠播放启动: "${nextItem.text.substring(0, 30)}..."`,
      "warn"
    );

    // 🔧 关键改进：预先设置极低音量，避免突然出现
    nextItem.howl.volume(0.01);

    // 🔧 确保下一个音频已经准备好
    if (nextItem.howl.state() !== "loaded") {
      this.logMobile("下一个音频未加载完成，强制加载", "warn");
      nextItem.howl.load();
    }

    // 立即开始播放下一个音频
    nextItem.howl.play();

    // 🔧 核心修复：立即切换引用，然后进行音量调整
    this.performTrueSeamlessSwitch(nextItem);
  }

  // 🔧 新增：执行真正的无缝切换（确保始终有声音）
  private performTrueSeamlessSwitch(nextItem: HowlerAudioItem) {
    this.logMobile("开始真正无缝切换，确保连续播放", "warn");

    // 🔧 关键：保存旧音频引用，立即切换到新音频
    const oldHowl = this.currentHowl;
    this.currentHowl = nextItem.howl;

    // 🔧 立即重置标志和设置监听器，确保状态正确
    this.isOverlapPlaying = false;
    this.setupSeamlessOverlapPlay(nextItem.howl, nextItem);
    this.isPlaying = true;
    this.startProgressTimer();
    // 记录启动信息用于防重复
    try {
      this.lastStartedItemId = nextItem?.id || null;
      this.lastStartedAt = Date.now();
    } catch {}

    this.logMobile("音频引用已切换，开始音量平滑过渡", "info");

    // 🍎 iOS/Safari 后台：硬停止旧音频，避免后台双声
    const isBackgroundSafari =
      this.isSafari() && typeof document !== "undefined" && document.hidden;
    if (isBackgroundSafari) {
      try {
        if (oldHowl) {
          try {
            oldHowl.volume(0);
          } catch {}
          try {
            oldHowl.stop();
          } catch {}
        }
        if (nextItem.howl) {
          const target = this.instanceMuted ? 0 : this.instanceVolume;
          try {
            nextItem.howl.volume(target);
          } catch {}
        }
      } catch {}
    } else {
      // 前台/非 Safari：使用交叉淡入淡出（无固定时长，按曲目时长自适应，不使用定时停止）
      try {
        const target = this.instanceMuted ? 0 : this.instanceVolume;
        const d = Math.max(
          60,
          Math.min(250, Math.floor((nextItem?.duration || 1) * 80))
        );
        if (nextItem.howl) {
          try {
            nextItem.howl.fade(0.01, target, d);
          } catch {
            nextItem.howl.volume(target);
          }
        }
        if (oldHowl) {
          try {
            oldHowl.fade(oldHowl.volume(), 0.0, d);
          } catch {
            oldHowl.volume(0.0);
          }
          // 让旧音频自然结束，避免固定时间 stop()
        }
      } catch {
        if (oldHowl) {
          try {
            oldHowl.volume(0.0);
          } catch {}
        }
        if (nextItem.howl) {
          try {
            nextItem.howl.volume(this.instanceVolume);
          } catch {}
        }
      }
    }
  }

  // 🔧 新增：移动端调试日志记录
  private logMobile(message: string, type: "info" | "warn" | "error" = "info") {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = {
      timestamp: timestamp,
      message: message,
      type: type
    };

    // 添加到移动端日志
    this.mobileDebugLogs.unshift(logEntry);

    // 保持日志数量限制
    if (this.mobileDebugLogs.length > this.maxDebugLogs) {
      this.mobileDebugLogs = this.mobileDebugLogs.slice(0, this.maxDebugLogs);
    }

    // 同时输出到控制台（仅开发环境）
    if (this.isMobileDevice()) {
      const prefix = "📱";
      switch (type) {
        case "error":
          console.error(`${prefix} ${message}`);
          break;
        case "warn":
          console.warn(`${prefix} ${message}`);
          break;
        default:
          console.log(`${prefix} ${message}`);
      }
    }

    // 同步到移动端日志面板/文件
    try {
      emitter.emit("mobileAudioLog", {
        level: type,
        source: "howler",
        message,
        timestamp: Date.now()
      });
    } catch {}
  }

  private initHowler() {
    // 🔧 关键修复：不设置全局配置，避免多房间冲突
    // 恢复原来的简单配置
    if (!HowlerSeamlessPlayer.isGlobalInitialized) {
      Howler.html5PoolSize = 18;
      HowlerSeamlessPlayer.isGlobalInitialized = true;
    }

    // 监听可见性变化：用于移动端前后台策略（可配置）
    if (typeof document !== "undefined") {
      try {
        this.visibilityHandler = () => {
          const hidden = document.hidden;
          this.logMobile(
            `🍎 页面可见性变化: hidden=${hidden}, Safari=${this.isSafari()}`,
            "info"
          );

          // 检查配置并记录
          const shouldPause = this.shouldPauseOnHidden();
          console.log(
            `🍎 visibilitychange事件: hidden=${hidden}, shouldPause=${shouldPause}, roomId=${this.roomId}`
          );

          // 🍎 Safari专用：后台时调整定时器策略
          if (this.isSafari()) {
            if (hidden) {
              this.logMobile(
                `🍎 Safari进入后台模式：降低轮询频率，依赖onend事件`,
                "warn"
              );
              // 重新启动进度监控（会自动使用较低的频率）
              if (this.isPlaying) {
                this.startProgressTimer();
              }
            } else {
              this.logMobile(`🍎 Safari回到前台模式：恢复正常轮询频率`, "info");
              // 回到前台先尝试解锁，降低自动播放受限概率
              try {
                (HowlerSeamlessPlayer as any).unlock &&
                  (HowlerSeamlessPlayer as any).unlock();
              } catch {}

              // 清理：移除前台救援逻辑，避免与交接并发；仅调整轮询与解锁，不自动发起播放

              // 重新启动进度监控（会自动使用正常频率）
              if (this.isPlaying) {
                this.startProgressTimer();
              }
            }
          }

          // 仅在需要时暂停：iOS 或显式配置开启
          if (this.shouldPauseOnHidden()) {
            if (hidden) {
              this.wasPlayingBeforeHidden = this.isPlaying;
              if (this.isPlaying) {
                try {
                  this.pause();
                } catch {}
              }
            } else {
              if (this.wasPlayingBeforeHidden) {
                // 前台先解锁，再恢复
                try {
                  (HowlerSeamlessPlayer as any).unlock &&
                    (HowlerSeamlessPlayer as any).unlock();
                } catch {}
                try {
                  this.resume();
                } catch {}
                this.wasPlayingBeforeHidden = false;
              }
            }
          }
        };
        document.addEventListener("visibilitychange", this.visibilityHandler, {
          passive: true
        });
      } catch {}
    }
  }

  // 🔧 新增：全局初始化标记，避免重复初始化
  private static isGlobalInitialized = false;

  // 🔧 新增：根据环境与设备决定是否在隐藏时暂停（收口到最优：仅 iOS/iPadOS 暂停）
  private shouldPauseOnHidden(): boolean {
    try {
      const cfg: any = getConfig();
      console.log("🍎 后台播放配置:", {
        config: cfg,
        IOSAllowBackgroundPlay: cfg?.IOSAllowBackgroundPlay,
        isIOSLike: this.isIOSLike()
      });
      // 当允许 iOS 后台播放时，不在隐藏时暂停
      if (cfg && cfg.IOSAllowBackgroundPlay === true) {
        console.log("🍎 允许后台播放，不暂停");
        return false;
      }
    } catch (e) {
      console.error("🍎 读取配置失败:", e);
    }
    const shouldPause = this.isIOSLike();
    console.log(`🍎 后台播放决策: shouldPause=${shouldPause}`);
    return shouldPause;
  }

  // 🔧 修复：设置实例级音量，不影响其他房间
  setVolume(volume: number) {
    this.instanceVolume = volume;
    console.warn(`📱 房间${this.roomId} 设置音量: ${volume}`);

    // 只设置当前播放的音频音量，不设置全局音量
    if (this.currentHowl) {
      this.currentHowl.volume(this.instanceMuted ? 0 : volume);
    }

    // 设置播放列表中所有音频的音量
    this.playlist.forEach(item => {
      if (item.howl) {
        item.howl.volume(this.instanceMuted ? 0 : volume);
      }
    });
  }

  // 🔧 修复：设置实例级静音，不影响其他房间
  setMuted(muted: boolean) {
    this.instanceMuted = muted;
    console.warn(`📱 房间${this.roomId} 设置静音: ${muted}`);

    // 只设置当前播放的音频静音状态，不设置全局静音
    if (this.currentHowl) {
      this.currentHowl.volume(muted ? 0 : this.instanceVolume);
    }

    // 设置播放列表中所有音频的静音状态
    this.playlist.forEach(item => {
      if (item.howl) {
        item.howl.volume(muted ? 0 : this.instanceVolume);
      }
    });
  }

  // 添加音频到播放列表
  async addToPlaylist(item: HowlerAudioItem) {
    console.warn(
      `📱 Howler: 添加音频到播放列表: "${item.text.substring(0, 30)}..."`
    );

    // 🍎 Safari专用：检查是否需要无缝拼接到当前播放
    if (
      this.isSafari() &&
      this.isPlaying &&
      this.currentHowl &&
      this.playlist.length > 0
    ) {
      const shouldSeamlessAppend = await this.trySeamlessAppend(item);
      if (shouldSeamlessAppend) {
        this.logMobile(
          `🍎 Safari无缝拼接成功: "${item.text.substring(0, 30)}..."`,
          "info"
        );
        return;
      }
    }

    // 创建Howl实例
    const useHtml5 = this.isMobileDevice() ? true : !this.shouldUseWebAudio();
    const howl = new Howl({
      src: [item.audioUrl],
      html5: useHtml5, // 移动端强制 HTML5，提高后台稳定性
      preload: true, // 恢复原来的预加载设置
      format: ["mp3", "wav", "aac"],
      autoplay: false,
      loop: false,
      // 🔧 优化移动端音频缓冲，减少停顿感
      ...(this.isMobileDevice() && {
        buffer: true, // 移动端启用缓冲
        pool: 1 // 移动端减少并发，避免资源竞争
      }),
      onload: () => {
        this.logMobile(`音频加载完成: ${howl.duration().toFixed(2)}s`, "info");
        item.duration = howl.duration();
        // iOS 专项：确保 HTML5 <audio> 启用行内播放与预加载
        try {
          const node = (howl as any)?._sounds?.[0]?._node as HTMLAudioElement;
          if (node) {
            node.setAttribute("playsinline", "true");
            node.setAttribute("webkit-playsinline", "true");
            node.preload = "auto";
          }
        } catch {}
      },
      onloaderror: (id, error) => {
        this.logMobile(
          `${transformI18n($t("myLiveRoomPage.info.audioLoadFailed"))}: ${error}`,
          "error"
        );
        this.recordErrorBurst();
        this.logMobile(`: ${error}`, "error");
        this.recordErrorBurst();
        if (this.isErrorBursting()) {
          this.logMobile("检测到音频错误爆发，触发熔断一轮", "error");
          this.isPlaying = false;
          this.stopProgressTimer();
          return;
        }
        // 跳过当前故障音频，尝试播放下一条，避免卡住
        try {
          this.currentIndex = Math.min(
            this.currentIndex + 1,
            this.playlist.length
          );
          this.isPlaying = false;
          this.playNext();
        } catch {}
      },
      onplay: () => {
        // 🔧 关键修复：只有在不等待进度恢复时才处理播放事件
        if (this.waitingForProgressRestore) {
          this.logMobile(`等待进度恢复中，忽略onplay事件`, "info");
          return;
        }

        this.logMobile(`开始播放: "${item.text.substring(0, 30)}..."`, "warn");
        this.isPlaying = true;
        // 首次播放：直接将音量设为目标值（不使用固定延时淡入）
        try {
          if (!this.initialFadeInDone) {
            const target = this.instanceMuted ? 0 : this.instanceVolume;
            try {
              howl.volume(target);
            } catch {}
            if (target > 0) this.initialFadeInDone = true;
          }
        } catch {}
        this.startProgressTimer();

        // 🎵 关键：为新播放的音频设置无缝播放监听器
        this.setupSeamlessOverlapPlay(howl, item);

        // 同步绑定底层 HTMLAudioElement 事件作为兜底
        try {
          const node = (howl as any)?._sounds?.[0]?._node as HTMLAudioElement;
          if (node) {
            this.detachCoreEndedListener(node);
            const onEnded = () => {
              try {
                const seek =
                  typeof howl.seek === "function" ? (howl.seek() as number) : 0;
                const state =
                  typeof howl.state === "function" ? howl.state() : "loaded";
                const justStarted =
                  this.lastStartedItemId === item.id &&
                  Date.now() - this.lastStartedAt < 1000;
                const notReady = state !== "loaded";
                if (
                  notReady ||
                  (!howl.playing() && (seek === 0 || Number.isNaN(seek))) ||
                  (justStarted && seek <= 0.05)
                ) {
                  this.logMobile(
                    "忽略尚未开始播放时收到的核心 ended 事件",
                    "info",
                    {
                      event: "preplay_ended_core_ignored",
                      seek,
                      state,
                      justStarted
                    }
                  );
                  return;
                }
              } catch {}

              if (!this.hasTriggeredCompletion) {
                this.logMobile(
                  '底层 ended 事件：触发完成 {"cause":"htmlaudio_onended"}',
                  "warn"
                );
                this.hasTriggeredCompletion = true;
                this.handleManualAudioComplete("htmlaudio_onended");
              }
            };
            node.addEventListener("ended", onEnded);
            (node as any).__coreBound = { onEnded };
          }
        } catch {}

        // 🍎 Safari专用：设置Media Session以支持后台播放控制
        if (this.isSafari() || this.isIOSLike()) {
          this.setupSafariMediaSession(item);
        }

        // 🔧 新增：电脑端预防电流声 - 设置即将结束时的淡出监听
        if (!this.isMobileDevice()) {
          this.setupPreEndFadeOut(howl);
        }
      },
      onend: () => {
        // 🍎 Safari专用：使用自然结束事件作为主推进，避免依赖高频轮询
        if (!this.hasTriggeredCompletion) {
          this.hasTriggeredCompletion = true;
          this.logMobile(
            `🍎 Safari onend事件触发: "${item.text.substring(0, 30)}..."`,
            "info"
          );

          // 🍎 Safari后台专用：确保状态同步
          if (
            this.isSafari() &&
            typeof document !== "undefined" &&
            document.hidden
          ) {
            this.logMobile(`🍎 Safari后台onend: 强制状态同步`, "warn");
            this.isPlaying = false;
            // 清理所有定时器
            if (this.seamlessProgressTimer) {
              clearInterval(this.seamlessProgressTimer);
              this.seamlessProgressTimer = undefined;
            }
            // 解绑事件驱动的进度监听
            this.stopProgressTimer();
          }

          this.handleManualAudioComplete();
        }

        // 移除固定延迟保险机制：依赖 onended/进度事件自然推进
      },
      onplayerror: (id, error) => {
        this.logMobile(`播放错误: ${error}`, "error");
        this.recordErrorBurst();

        // 移动端：优先判定是否为权限/交互限制错误（不可在无用户手势下播放）
        if (this.isMobileDevice()) {
          const msg = (error && (error.message || String(error))) || "";
          const notAllowed =
            /NotAllowed|not\s+allowed|user\s*interaction|gesture|The\s*request\s*is\s*not\s*allowed/i.test(
              msg
            );
          if (notAllowed) {
            this.logMobile(
              "移动端权限受限：保留当前索引，等待解锁/用户交互",
              "warn"
            );
            this.isPlaying = false;
            this.stopProgressTimer();
            // 通知可见交接：前台起播失败，取消 handoff 以避免静音，由 WA 继续
            try {
              emitter.emit("apple:howler-not-allowed", { roomId: this.roomId });
            } catch {}
            try {
              (HowlerSeamlessPlayer as any).unlock &&
                (HowlerSeamlessPlayer as any).unlock();
            } catch {}
            return;
          }
          // 其他移动端错误：跳过当前尝试下一条
          this.logMobile(
            '移动端播放错误，尝试跳过当前继续下一条 {"cause":"onplayerror"}',
            "warn"
          );
          this.errorBurstCount = 0;
          this.currentIndex++;
          this.playNext("onplayerror");
          return;
        }

        if (this.isErrorBursting()) {
          this.logMobile("检测到播放错误爆发，暂缓重试", "error");
          this.isPlaying = false;
          this.stopProgressTimer();
          return;
        }
        // WebAudio 失败时回退到 HTML5
        try {
          const useWebAudio = this.shouldUseWebAudio();
          if (useWebAudio && howl && (howl as any)._webAudio) {
            // 销毁当前实例并回退
            try {
              howl.unload();
            } catch {}
            const fallback = new Howl({
              src: [item.audioUrl],
              html5: true,
              preload: true,
              format: ["mp3", "wav", "aac"],
              autoplay: false,
              loop: false,
              onload: () => {
                item.duration = fallback.duration();
              },
              onplay: () => {
                this.setupSeamlessOverlapPlay(fallback, item);
              }
            });
            item.howl = fallback;
            // 立即切换并播放（Apple hidden 下不启动）
            this.currentHowl = fallback;
            if (
              typeof shouldBlockHowlerInAppleHidden === "function" &&
              shouldBlockHowlerInAppleHidden()
            ) {
              this.logMobile(
                "🍎 iOS hidden: skip Howler fallback.play(); WebAudio handles scheduling",
                "warn"
              );
              logAppleGuard("skip fallback.play() in hidden state");
              return;
            }
            fallback.play();
            this.isPlaying = true;
            this.startProgressTimer();
            return;
          }
        } catch {}
        this.playNext("fallback_failed");
      }
    });

    item.howl = howl;

    // 🔧 关键修复：设置新音频的音量状态，使用实例级设置
    howl.volume(this.instanceMuted ? 0 : this.instanceVolume);

    this.playlist.push(item);

    // 🔧 优化：移动端预加载策略，减少音频切换停顿
    if (this.isMobileDevice()) {
      // 移动端：预加载下一个音频，确保切换流畅
      this.preloadNextAudioForMobile();
    }

    // 关闭内部自动播放，避免与上层触发竞态导致“重复起播”
    if (this.waitingForProgressRestore) {
      console.log(`📱 Howler: 等待进度恢复完成，跳过自动播放`);
    }
  }

  // 播放下一个音频（带 cause 便于日志追踪）
  private playNext(cause: string = "internal") {
    try {
      const hidden = typeof document !== "undefined" ? document.hidden : false;
      const hasGuard =
        typeof (globalThis as any).shouldBlockHowlerInAppleHidden ===
        "function";
      const guardDecision = hasGuard
        ? (globalThis as any).shouldBlockHowlerInAppleHidden()
        : "absent";
      const handoffActive = (() => {
        try {
          const g: any = (globalThis as any).__appleHandoffActiveRooms;
          return !!(g && typeof g.has === "function" && g.has(this.roomId));
        } catch {
          return false;
        }
      })();
      const status = {
        cause,
        hidden,
        guard: String(guardDecision),
        freeze: this.appleHiddenFreeze,
        handoffActive,
        waiting: this.waitingForProgressRestore,
        isPlaying: this.isPlaying,
        idx: `${this.currentIndex}/${this.playlist.length}`
      } as any;
      this.logMobile(
        `[probe] HOWLER_PLAYNEXT_ENTRY ${JSON.stringify(status)}`,
        hidden ? "warn" : "info"
      );
    } catch {}
    // 🍎 隐藏态冻结：不在后台由 Howler 继续下一条（交给 WebAudio 分支）
    if (
      this.appleHiddenFreeze &&
      this.isSafari() &&
      typeof document !== "undefined" &&
      document.hidden
    ) {
      this.logMobile(
        "🍎 隐藏态冻结：Howler 不继续下一条，由 WebAudio 接管",
        "warn"
      );
      return;
    }
    // 互斥锁：避免后台并发触发
    if (this._playNextLock) {
      this.logMobile("单次触发锁定：忽略并发 playNext", "warn");
      return;
    }
    this._playNextLock = true;
    try {
      // 去除基于固定毫秒的防抖，改由互斥锁/令牌防并发
      this.logMobile(
        `playNext()被调用: 索引=${this.currentIndex}/${this.playlist.length}, 移动端=${this.isMobileDevice()} {"cause":"${cause}"}`,
        "warn"
      );

      // 🔧 关键修复：检查是否在等待进度恢复
      if (this.waitingForProgressRestore) {
        this.logMobile(
          '等待进度恢复中，跳过playNext() {"at":"playNext"}',
          "warn"
        );

        // 🔧 防死锁：如果等待进度恢复超过3秒，强制清除等待标志
        const now = Date.now();
        if (!this.progressRestoreStartTime) {
          this.progressRestoreStartTime = now;
        } else if (now - this.progressRestoreStartTime > 3000) {
          this.logMobile(
            "⚠️ 进度恢复等待超时，强制清除等待标志防止死锁",
            "error"
          );
          this.waitingForProgressRestore = false;
          this.progressRestoreStartTime = 0;
          // 继续执行playNext，不要return
        } else {
          return;
        }
      } else {
        // 清除进度恢复开始时间
        this.progressRestoreStartTime = 0;
      }

      if (this.currentIndex >= this.playlist.length) {
        this.logMobile("播放列表结束", "warn");
        this.isPlaying = false;
        this.stopProgressTimer();
        return;
      }

      const item = this.playlist[this.currentIndex];
      this.logMobile(
        `准备播放当前索引: ${this.currentIndex} / ${this.playlist.length} itemValid=${!!item} howl=${!!item?.howl}`,
        "info"
      );
      if (!item || !item.howl) {
        this.logMobile(
          `音频项目无效: 索引=${this.currentIndex}, item=${!!item}, howl=${!!item?.howl} {"cause":"${cause}"}`,
          "error"
        );
        this.currentIndex++;
        this.playNext("invalid_item");
        return;
      }

      const seq = ++this.playSeqCounter;
      this.logMobile(
        `将要播放: (${this.currentIndex + 1}/${this.playlist.length}) "${item.text.substring(0, 30)}..." {"cause":"${cause}","trackId":"${item.id}","playSeq":${seq}}`,
        "warn"
      );

      // 🍎 Apple hidden guard: never start a new Howler play() in iOS/Safari background
      if (
        typeof shouldBlockHowlerInAppleHidden === "function" &&
        shouldBlockHowlerInAppleHidden()
      ) {
        this.logMobile(
          "🍎 iOS hidden: skip Howler trigger; WebAudio will handle scheduling.",
          "warn"
        );
        logAppleGuard("skip playNext() start in hidden state");
        return;
      }

      // 🔁 防重复：若当前已在播且目标与当前为同一 howl/同一ID，且为短时间内的重复调用，则忽略
      try {
        const sameHowl = !!(
          this.currentHowl &&
          item?.howl &&
          this.currentHowl === item.howl
        );
        const sameId = !!(
          this.lastStartedItemId &&
          item?.id &&
          this.lastStartedItemId === item.id
        );
        const withinShort = Date.now() - this.lastStartedAt < 1500;
        if (
          this.currentHowl &&
          (sameHowl || (sameId && withinShort)) &&
          this.currentHowl.playing()
        ) {
          this.logMobile(
            "检测到同一音频的重复 playNext，忽略以避免重播",
            "warn"
          );
          return;
        }
      } catch {}

      // 🔧 优化移动端音频切换：避免“误判已有当前音频”导致的卡住
      // 仅在“确实仍在输出且未到尾声”的情况下，才阻止起播；
      // 若已接近尾声或刚结束（seek≈duration），则允许直接起播下一条。
      if (
        this.currentHowl &&
        typeof (this.currentHowl as any).playing === "function"
      ) {
        let stillPlaying = false;
        let nearEnd = false;
        try {
          stillPlaying = this.currentHowl.playing();
          const curT = Number(this.currentHowl.seek() as number) || 0;
          const dur =
            Number(
              (this.currentHowl as any).duration &&
                (this.currentHowl as any).duration()
            ) || 0;
          nearEnd = dur > 0 && curT >= Math.max(0, dur - 0.2);
        } catch {}
        if (stillPlaying && !nearEnd) {
          // 真正还在播，完全依赖 onended/进度事件推进，不再设固定复查
          this.logMobile("已有当前音频，等待自然结束，不进行切换", "warn");
          return;
        }
      }

      this.logMobile(
        `无当前音频，直接播放: "${item.text.substring(0, 30)}..." {"cause":"${cause}","trackId":"${item.id}"}`
      );
      this.currentHowl = item.howl;

      // 开始播放（首播前先将音量设为0，避免被autoplay策略拦截）
      try {
        // 🔧 复位底层 HTMLAudioElement，避免复用时立即 onended
        try {
          const node = (this.currentHowl as any)?._sounds?.[0]?._node as
            | HTMLAudioElement
            | undefined;
          if (node) {
            try {
              node.pause();
            } catch {}
            try {
              node.currentTime = 0;
            } catch {}
            try {
              (node as any).ended = false;
            } catch {}
          }
        } catch {}
        if (!this.initialFadeInDone && this.currentHowl) {
          try {
            this.currentHowl.volume(0);
          } catch {}
        }
        this.currentHowl.play();
        // 播放启动已发起，真实“可听”由 onplay+timeupdate 判定后再行记录
      } catch (error) {
        this.logMobile(`音频播放启动失败: ${error}`, "error");
      }

      // 🎵 关键：为直接播放的音频设置无缝播放监听器（进度与可听判定在 onplay 中处理）
      this.setupSeamlessOverlapPlay(this.currentHowl, item);
      // 记录启动信息用于防重复
      try {
        this.lastStartedItemId = item?.id || null;
        this.lastStartedAt = Date.now();
      } catch {}
      this.logMobile(
        `playNext()完成（等待 onplay 可听确认） {"cause":"${cause}","trackId":"${item.id}","playSeq":${seq}}`
      );
    } finally {
      this._playNextLock = false;
    }
  }

  //

  // 🔧 新增：电脑端平滑音频切换（淡出+淡入）
  private fadeOutAndSwitch(nextItem: HowlerAudioItem) {
    // 🍎 Apple hidden guard: avoid switching/starting in background
    if (
      typeof shouldBlockHowlerInAppleHidden === "function" &&
      shouldBlockHowlerInAppleHidden()
    ) {
      this.logMobile(
        "🍎 iOS hidden: skip Howler fadeOutAndSwitch; WebAudio handles next",
        "warn"
      );
      logAppleGuard("skip fadeOutAndSwitch in hidden state");
      return;
    }
    if (!this.currentHowl) {
      this.currentHowl = nextItem.howl;
      this.currentHowl.play();
      this.isPlaying = true;
      this.startProgressTimer();
      return;
    }

    const currentVolume = this.currentHowl.volume();
    // 使用 Howl.fade 事件式包络，不使用自建计时器
    try {
      this.currentHowl.fade(currentVolume, 0.1, 60);
    } catch {
      this.currentHowl.volume(0.1);
    }

    // 切换音频
    try {
      this.currentHowl.stop();
    } catch {}
    this.currentHowl = nextItem.howl;
    this.currentHowl.volume(this.instanceMuted ? 0 : this.instanceVolume);
    this.currentHowl.play();
    if (!this.isMobileDevice()) {
      this.fadeInAudio(
        this.currentHowl,
        this.instanceMuted ? 0 : this.instanceVolume,
        120
      );
    }
    this.setupSeamlessOverlapPlay(this.currentHowl, nextItem);
    this.isPlaying = true;
    this.startProgressTimer();
    try {
      this.lastStartedItemId = nextItem?.id || null;
      this.lastStartedAt = Date.now();
    } catch {}
  }

  // 🔧 新增：移动端快速音频切换（减少停顿感）
  private quickFadeOutAndSwitch(nextItem: HowlerAudioItem) {
    // 🍎 Apple hidden guard: avoid switching/starting in background
    if (
      typeof shouldBlockHowlerInAppleHidden === "function" &&
      shouldBlockHowlerInAppleHidden()
    ) {
      this.logMobile(
        "🍎 iOS hidden: skip Howler quickFadeOutAndSwitch; WebAudio handles next",
        "warn"
      );
      logAppleGuard("skip quickFadeOutAndSwitch in hidden state");
      return;
    }
    if (!this.currentHowl) {
      this.currentHowl = nextItem.howl;
      this.currentHowl.play();
      this.isPlaying = true;
      this.startProgressTimer();
      return;
    }

    // 移动端：使用更快的淡出时间，减少停顿感
    const currentVolume = this.currentHowl.volume();
    try {
      this.currentHowl.fade(currentVolume, 0.1, 40);
    } catch {
      this.currentHowl.volume(0.1);
    }
    try {
      this.currentHowl.stop();
    } catch {}
    this.currentHowl = nextItem.howl;
    this.currentHowl.volume(this.instanceMuted ? 0 : this.instanceVolume);
    this.currentHowl.play();
    this.quickFadeInAudio(
      this.currentHowl,
      this.instanceMuted ? 0 : this.instanceVolume,
      60
    );
    this.setupSeamlessOverlapPlay(this.currentHowl, nextItem);
    this.isPlaying = true;
    this.startProgressTimer();
    try {
      this.lastStartedItemId = nextItem?.id || null;
      this.lastStartedAt = Date.now();
    } catch {}
  }

  // 🔧 新增：音频淡入效果
  private fadeInAudio(howl: Howl, targetVolume: number, duration: number) {
    try {
      howl.fade(0.0, targetVolume, duration);
    } catch {
      howl.volume(targetVolume);
    }
  }

  // 🔧 新增：移动端快速音频淡入效果（减少停顿感）
  private quickFadeInAudio(howl: Howl, targetVolume: number, duration: number) {
    try {
      howl.fade(0.0, targetVolume, duration);
    } catch {
      howl.volume(targetVolume);
    }
  }

  // 🔧 新增：电脑端预防结尾电流声 - 在音频即将结束前进行短淡出
  private setupPreEndFadeOut(howl: Howl) {
    const checkNearEnd = () => {
      if (!howl || !howl.playing()) {
        return;
      }

      const duration = howl.duration();
      const currentTime = howl.seek() as number;
      const timeLeft = duration - currentTime;

      // 当剩余时间很短时，用 Howl.fade 做一次短淡出（无自建计时器）
      if (timeLeft > 0 && timeLeft <= 0.05) {
        try {
          howl.fade(
            howl.volume(),
            0.0,
            Math.max(10, Math.min(80, Math.floor(timeLeft * 1000)))
          );
        } catch {}
      }
    };

    const tick = () => {
      if (!howl || !howl.playing()) return;
      checkNearEnd();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  // 🍎 Safari专用：设置Media Session支持后台播放控制
  private setupSafariMediaSession(item: HowlerAudioItem) {
    if (!("mediaSession" in navigator)) {
      return;
    }

    try {
      const mediaSession = (navigator as any).mediaSession;

      // 设置媒体元数据
      mediaSession.metadata = new (window as any).MediaMetadata({
        title:
          item.text.length > 50
            ? item.text.substring(0, 47) + "..."
            : item.text,
        artist: "AI直播助手",
        album: "语音播报"
      });

      // 设置播放状态
      mediaSession.playbackState = this.isPlaying ? "playing" : "paused";

      // 🍎 Safari专用：绑定媒体控制事件（仅绑定一次）
      if (!(mediaSession as any).__safariHandlersBound) {
        mediaSession.setActionHandler("play", () => {
          if (this.currentHowl && !this.currentHowl.playing()) {
            this.resume();
          }
        });

        mediaSession.setActionHandler("pause", () => {
          if (this.currentHowl && this.currentHowl.playing()) {
            this.pause();
          }
        });

        mediaSession.setActionHandler("nexttrack", () => {
          if (this.currentIndex + 1 < this.playlist.length) {
            this.currentIndex++;
            this.playNext();
          }
        });

        mediaSession.setActionHandler("previoustrack", () => {
          if (this.currentIndex > 0) {
            this.currentIndex--;
            this.playNext();
          }
        });

        (mediaSession as any).__safariHandlersBound = true;
        this.logMobile(`🍎 Safari Media Session 控制器已设置`, "info");
      }
    } catch (error) {
      this.logMobile(`🍎 Safari Media Session 设置失败: ${error}`, "warn");
    }
  }

  // 音频播放完成回调（已禁用，使用handleManualAudioComplete替代）
  // private onAudioComplete() {
  //   // 已禁用，使用手动触发机制替代
  // }

  // 开始播放
  async play() {
    // 🔧 移除过于激进的健康检查，避免在产品切换时误判状态
    // 只有在明确的异常情况下才进行检查和修复

    if (this.playlist.length === 0) {
      console.warn("📱 Howler: 播放列表为空");
      return;
    }

    // 🔧 关键修复：检查是否在等待进度恢复
    if (this.waitingForProgressRestore) {
      console.log(`📱 Howler: 等待进度恢复中，跳过play()调用`);
      return;
    }

    if (!this.isPlaying) {
      try {
        const hidden =
          typeof document !== "undefined" ? document.hidden : false;
        const hasGuard =
          typeof (globalThis as any).shouldBlockHowlerInAppleHidden ===
          "function";
        const guardDecision = hasGuard
          ? (globalThis as any).shouldBlockHowlerInAppleHidden()
          : "absent";
        if (hidden) {
          this.logMobile(
            `[probe] HOWLER_PLAY_REQUEST hidden=${hidden} guard=${String(guardDecision)} freeze=${this.appleHiddenFreeze}`,
            "warn"
          );
        }
      } catch {}
      this.playNext();
    }
  }

  // 🔧 新增：从特定时间开始播放当前音频
  async playFromTime(seekTime: number) {
    if (this.playlist.length === 0) {
      console.warn("📱 Howler: 播放列表为空，无法从特定时间播放");
      return;
    }

    const currentItem = this.playlist[this.currentIndex];
    if (!currentItem || !currentItem.howl) {
      console.warn("📱 Howler: 当前音频无效，无法从特定时间播放");
      return;
    }

    console.warn(
      `📱 Howler: 从 ${seekTime.toFixed(2)}s 开始播放: "${currentItem.text.substring(0, 30)}..."`
    );

    // 停止之前的音频
    if (this.currentHowl) {
      this.currentHowl.stop();
    }

    this.currentHowl = currentItem.howl;

    // 设置播放位置
    this.currentHowl.seek(seekTime);

    // 🍎 Apple hidden guard: avoid starting in background
    try {
      const hidden = typeof document !== "undefined" ? document.hidden : false;
      const hasGuard =
        typeof (globalThis as any).shouldBlockHowlerInAppleHidden ===
        "function";
      const guardDecision = hasGuard
        ? (globalThis as any).shouldBlockHowlerInAppleHidden()
        : "absent";
      if (hidden) {
        this.logMobile(
          `[probe] HOWLER_PLAYFROMTIME_REQUEST hidden=${hidden} guard=${String(guardDecision)} freeze=${this.appleHiddenFreeze}`,
          "warn"
        );
      }
    } catch {}
    if (
      typeof shouldBlockHowlerInAppleHidden === "function" &&
      shouldBlockHowlerInAppleHidden()
    ) {
      this.logMobile(
        "🍎 iOS hidden: skip Howler playFromTime; WebAudio handles scheduling",
        "warn"
      );
      logAppleGuard("skip playFromTime() start in hidden state");
      return;
    }
    // 开始播放
    this.currentHowl.play();
    this.isPlaying = true;
    this.startProgressTimer();
  }

  // 🔧 新增：设置等待进度恢复标志
  setWaitingForProgressRestore(waiting: boolean) {
    this.waitingForProgressRestore = waiting;
    console.log(`📱 Howler: 设置等待进度恢复标志: ${waiting}`);
  }

  // 🔧 新增：检查是否在等待进度恢复
  isWaitingForProgressRestore(): boolean {
    return this.waitingForProgressRestore;
  }

  // 🔧 新增：移动端专用状态检查和诊断
  getMobileDebugInfo() {
    const nextIndex = this.currentIndex + 1;
    const nextItem = this.playlist[nextIndex];
    return {
      isMobile: this.isMobileDevice(),
      currentIndex: this.currentIndex,
      totalItems: this.playlist.length,
      isPlaying: this.isPlaying,
      hasNext: nextIndex < this.playlist.length,
      nextItemExists: !!nextItem,
      nextItemHowl: !!nextItem?.howl,
      nextItemState: nextItem?.howl ? nextItem.howl.state() : "N/A",
      waitingForRestore: this.waitingForProgressRestore,
      currentItemText:
        this.playlist[this.currentIndex]?.text?.substring(0, 50) || "N/A",
      nextItemText: nextItem?.text?.substring(0, 50) || "N/A",
      roomId: this.roomId,
      debugLogs: this.mobileDebugLogs.slice(0, 10), // 返回最近10条日志
      currentTime: this.currentHowl ? (this.currentHowl.seek() as number) : 0,
      duration: this.playlist[this.currentIndex]?.duration || 0,
      // 🎵 新增：无缝播放状态
      isOverlapPlaying: this.isOverlapPlaying,
      hasTriggeredCompletion: this.hasTriggeredCompletion,
      seamlessTimerActive: !!this.seamlessProgressTimer,
      // 🍎 Safari专用调试信息
      isSafari: this.isSafari(),
      isIOSLike: this.isIOSLike(),
      isDocumentHidden:
        typeof document !== "undefined" ? document.hidden : false,
      mediaSessionSupported: "mediaSession" in navigator
    };
  }

  // 🔧 新增：移动端强制播放下一个（用于手动触发）
  forcePlayNextForMobile() {
    if (!this.isMobileDevice()) {
      this.logMobile("此方法仅用于移动端调试", "warn");
      return false;
    }

    this.logMobile("移动端强制播放下一个被调用", "warn");
    const debugInfo = this.getMobileDebugInfo();
    this.logMobile(
      `当前状态: 索引=${debugInfo.currentIndex}, 总数=${debugInfo.totalItems}, 正在播放=${debugInfo.isPlaying}`
    );

    if (debugInfo.hasNext) {
      this.currentIndex++;
      this.logMobile(`强制推进索引到: ${this.currentIndex}`, "warn");
      this.playNext();
      return true;
    } else {
      this.logMobile("没有下一个音频可播放", "warn");
      return false;
    }
  }

  // 🔧 新增：检测移动设备
  private isMobileDevice(): boolean {
    const ua = navigator.userAgent || "";
    const mobileUA =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isIpadOS =
      /Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1;
    return mobileUA || isIpadOS;
  }

  // 🔧 新增：iOS/iPadOS 判定（含iPadOS桌面UA）
  private isIOSLike(): boolean {
    const ua = navigator.userAgent || "";
    const iOS = /iPhone|iPad|iPod/i.test(ua);
    const iPadOS =
      /Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1;
    return iOS || iPadOS;
  }

  // 🍎 新增：Safari浏览器检测
  private isSafari(): boolean {
    const ua = navigator.userAgent || "";
    return /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
  }

  // 是否已解锁 WebAudio（需要一次用户手势后才可用）
  private isAudioUnlocked(): boolean {
    try {
      const ctx: any = (Howler as any).ctx || (Howler as any)._audioContext;
      return !!ctx && ctx.state === "running";
    } catch {
      return false;
    }
  }

  // 前台桌面优先 WebAudio；iOS/移动端或后台强制 HTML5，确保后台稳定
  private shouldUseWebAudio(): boolean {
    const isHidden = typeof document !== "undefined" && document.hidden;
    if (this.isMobileDevice()) return false;
    if (isHidden) return false;
    return true;
  }

  // 🔧 简单优化：预加载下一个音频
  private preloadNextAudio() {
    // 移除复杂的预加载逻辑，保持简单
  }

  // 🔧 新增：移动端专用预加载方法，减少音频切换停顿
  private preloadNextAudioForMobile() {
    const nextIndex = this.currentIndex + 1;
    if (nextIndex < this.playlist.length) {
      const nextItem = this.playlist[nextIndex];
      if (nextItem && nextItem.howl) {
        const state = nextItem.howl.state();
        this.logMobile(`检查下一个音频(${nextIndex})状态: ${state}`, "info");

        // 🔧 修复：正确判断state()返回值，只有unloaded或loading时才需要预加载
        if (state === "unloaded" || state === "loading") {
          // 预加载下一个音频，确保切换时无延迟
          try {
            this.logMobile(
              `开始预加载下一个音频: "${nextItem.text.substring(0, 30)}..."`,
              "info"
            );
            nextItem.howl.load();
          } catch (error) {
            this.logMobile(`预加载下一个音频失败: ${error}`, "error");
          }
        } else {
          this.logMobile(`下一个音频已加载(${state})，跳过预加载`, "info");
        }
      } else {
        this.logMobile(
          `下一个音频项目不存在: nextItem=${!!nextItem}, howl=${!!nextItem?.howl}`,
          "warn"
        );
      }
    } else {
      this.logMobile("已经是最后一个音频，无需预加载", "info");
    }
  }

  // 错误爆发统计与判定
  private recordErrorBurst() {
    const now = Date.now();
    if (now - this.lastErrorAt > 10000) {
      // 超过窗口，重置
      this.errorBurstCount = 0;
    }
    this.lastErrorAt = now;
    this.errorBurstCount++;
  }

  private isErrorBursting(): boolean {
    // 10秒内连续3次错误则判定为爆发
    return this.errorBurstCount >= 3 && Date.now() - this.lastErrorAt <= 10000;
  }

  // 🔧 简单优化：移动端音频缓冲
  private enhanceMobileBuffering(_howl: Howl) {
    // 移除复杂的缓冲逻辑，保持简单
  }

  // 暂停播放（保持状态，可恢复）
  pause() {
    if (this.currentHowl) {
      this.currentHowl.pause();
    }
    this.isPlaying = false;
    this.stopProgressTimer();
    try {
      if ("mediaSession" in navigator) {
        // @ts-ignore
        navigator.mediaSession.playbackState = "paused";
      }
    } catch {}
  }

  // 恢复播放
  resume() {
    if (this.currentHowl) {
      // 🍎 Apple hidden guard: avoid starting in background
      try {
        const hidden =
          typeof document !== "undefined" ? document.hidden : false;
        const hasGuard =
          typeof (globalThis as any).shouldBlockHowlerInAppleHidden ===
          "function";
        const guardDecision = hasGuard
          ? (globalThis as any).shouldBlockHowlerInAppleHidden()
          : "absent";
        if (hidden) {
          this.logMobile(
            `[probe] HOWLER_RESUME_REQUEST hidden=${hidden} guard=${String(guardDecision)} freeze=${this.appleHiddenFreeze}`,
            "warn"
          );
        }
      } catch {}
      if (
        typeof shouldBlockHowlerInAppleHidden === "function" &&
        shouldBlockHowlerInAppleHidden()
      ) {
        this.logMobile(
          "🍎 iOS hidden: skip Howler resume; WebAudio handles scheduling",
          "warn"
        );
        logAppleGuard("skip resume() start in hidden state");
        return;
      }
      this.currentHowl.play();
      this.isPlaying = true;
      this.startProgressTimer();
      try {
        if ("mediaSession" in navigator) {
          // @ts-ignore
          navigator.mediaSession.playbackState = "playing";
        }
      } catch {}
    }
  }

  // 🍎 仅苹果隐藏态使用：设置后台冻结，禁止 Howler 在隐藏态推进与起播
  setAppleHiddenFreeze(freeze: boolean) {
    this.appleHiddenFreeze = !!freeze;
  }

  // 🍎 设置一段时间内抑制前台强制完成检查（单位：ms）
  suppressForegroundForcedComplete(ms: number) {
    const dur = Math.max(0, Math.min(5000, Number(ms) || 0));
    this.suppressForcedCompleteUntil = Date.now() + dur;
  }

  // 平滑恢复到指定位置（避免卡顿）
  smoothResumeFromTime(seekTime: number) {
    if (!this.currentHowl) {
      return;
    }

    const currentTime = this.currentHowl.seek() as number;
    const timeDiff = Math.abs(currentTime - seekTime);

    console.log(
      `🎵 平滑恢复: 当前${currentTime.toFixed(2)}s -> 目标${seekTime.toFixed(2)}s (差异${timeDiff.toFixed(2)}s)`
    );

    // 🔧 如果时间差异很小（<1s），直接恢复播放，避免任何 seek 操作
    if (timeDiff < 1) {
      console.log(`🎵 时间差异很小(${timeDiff.toFixed(2)}s)，直接恢复播放`);
      if (!this.currentHowl.playing()) {
        this.currentHowl.play();
        this.isPlaying = true;
        this.startProgressTimer();
      }
      return;
    }

    // 🔧 提供无卡顿模式：如果差异不是太大，继续从当前位置播放
    if (timeDiff < 3) {
      console.log(
        `🎵 启用无卡顿模式：从当前位置(${currentTime.toFixed(2)}s)继续播放，跳过目标位置(${seekTime.toFixed(2)}s)`
      );
      if (!this.currentHowl.playing()) {
        this.currentHowl.play();
        this.isPlaying = true;
        this.startProgressTimer();
      }
      return;
    }

    // 🔧 只有在时间差异很大时(>3s)才进行 seek 操作
    console.log(`🎵 时间差异较大(${timeDiff.toFixed(2)}s)，执行 seek 操作`);
    if (this.currentHowl.playing()) {
      // 🔧 关键优化：降低音量 -> seek -> 恢复音量，减少 seek 时的噪音
      const originalVolume = this.currentHowl.volume();
      this.currentHowl.volume(0.05);

      // 无固定延迟：用 seeked 事件恢复音量
      try {
        const node = (this.currentHowl as any)?._sounds?.[0]?._node as
          | HTMLAudioElement
          | undefined;
        if (node) {
          const onSeeked = () => {
            try {
              this.currentHowl &&
                this.currentHowl.fade(
                  0.05,
                  originalVolume,
                  Math.max(
                    60,
                    Math.min(
                      200,
                      Math.floor((this.currentHowl.duration() || 1) * 80)
                    )
                  )
                );
            } catch {
              try {
                this.currentHowl?.volume(originalVolume);
              } catch {}
            }
            node.removeEventListener("seeked", onSeeked);
          };
          node.addEventListener("seeked", onSeeked, { once: true } as any);
        }
      } catch {}
      try {
        this.currentHowl.seek(seekTime);
      } catch {}
    } else {
      // 如果没有播放，正常设置位置后开始播放
      this.currentHowl.seek(seekTime);
      this.currentHowl.play();
      this.isPlaying = true;
      this.startProgressTimer();
    }
  }

  // 停止播放
  stop() {
    this.playlist.forEach(item => {
      if (item.howl) {
        item.howl.stop();
        item.howl.unload();
      }
    });

    this.playlist = [];
    this.currentIndex = 0;
    this.isPlaying = false;
    this.stopProgressTimer();
  }

  // 清空播放列表但不停止当前播放
  clearPlaylist() {
    this.playlist.forEach(item => {
      if (item.howl) {
        item.howl.stop();
        item.howl.unload();
      }
    });

    this.playlist = [];
    this.currentIndex = 0;
    this.isPlaying = false;
    this.stopProgressTimer();
  }

  // 开始进度监控
  private startProgressTimer() {
    this.stopProgressTimer();

    // 🍎 Safari专用：后台时使用更低频率，避免被严格节流
    const isSafariBackground = this.isSafari() && document.hidden;
    const isSafariForeground = this.isSafari() && !document.hidden;

    let interval: number;
    if (isSafariBackground) {
      interval = 1000; // Safari后台：1秒间隔，确保能正常执行
    } else if (isSafariForeground) {
      interval = 100; // Safari前台：100ms
    } else {
      interval = 50; // 其他浏览器：50ms
    }

    this.progressTimer = window.setInterval(() => {
      if (!this.isPlaying || this.currentIndex >= this.playlist.length) {
        return;
      }

      const currentItem = this.playlist[this.currentIndex];
      if (!currentItem?.duration || currentItem.duration === 0) {
        return;
      }

      const currentTime = this.currentHowl
        ? (this.currentHowl.seek() as number) || 0
        : 0;
      const progress =
        currentTime && currentItem.duration
          ? Math.min(currentTime / currentItem.duration, 1)
          : 0;

      if (this.onProgress) {
        this.onProgress(this.currentIndex, progress);
      }

      // 🍎 Safari后台专用：额外检查是否需要强制推进
      if (
        this.isSafari() &&
        document.hidden &&
        progress >= 0.95 &&
        !this.hasTriggeredCompletion
      ) {
        console.warn(
          `🍎 Safari后台强制检查：进度${(progress * 100).toFixed(1)}%，触发完成`
        );
        this.hasTriggeredCompletion = true;
        if (this.onProgress) {
          this.onProgress(this.currentIndex, 1);
        }
        setTimeout(() => {
          this.handleManualAudioComplete();
        }, 5);
      }
    }, interval);

    // 🔧 新增：移动端超时检查机制
    if (this.isMobileDevice()) {
      this.startMobileTimeoutCheck();
    }
  }

  // 🔧 新增：移动端超时检查机制
  private startMobileTimeoutCheck() {
    this.stopMobileTimeoutCheck();

    this.mobileTimeoutTimer = window.setInterval(() => {
      if (!this.isPlaying || !this.currentHowl) {
        return;
      }

      const currentItem = this.playlist[this.currentIndex];
      if (!currentItem || !currentItem.duration) {
        return;
      }

      const currentTime = this.currentHowl.seek() as number;
      const progress = currentTime / currentItem.duration;

      // 如果音频已经播放完成但没有继续，强制触发
      if (progress >= 0.99 && !this.currentHowl.playing()) {
        console.warn(
          `📱 移动端超时检查: 音频已经播放完成但没有继续，强制触发下一个`
        );
        this.stopMobileTimeoutCheck();
        // 延迟一点时间再触发，避免冲突
        setTimeout(() => {
          if (this.currentIndex < this.playlist.length - 1) {
            this.currentIndex++;
            this.playNext();
          }
        }, 200);
      }
    }, 1000); // 每秒1检查一次
  }

  // 🔧 新增：停止移动端超时检查
  private stopMobileTimeoutCheck() {
    if (this.mobileTimeoutTimer) {
      clearInterval(this.mobileTimeoutTimer);
      this.mobileTimeoutTimer = undefined;
    }
  }

  // 停止进度监控
  private stopProgressTimer() {
    // 清除定时器
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = undefined;
    }
    // 🔧 新增：同时停止移动端超时检查
    if (this.isMobileDevice()) {
      this.stopMobileTimeoutCheck();
    }
  }

  // 设置进度回调
  setProgressCallback(callback: (index: number, progress: number) => void) {
    this.onProgress = callback;
  }

  // 设置完成回调
  setCompleteCallback(callback: (index: number) => void) {
    this.onComplete = callback;
  }

  // 获取当前状态
  // 🔧 新增：检查播放器状态是否正常，防止频繁切换产品时出现问题
  isPlayerHealthy(): boolean {
    // 🔧 临时禁用健康检查，始终返回健康状态，避免干扰播放
    // 在产品切换时，状态不一致是正常的，不应该被认为是问题
    return true;

    // 原有的检查逻辑已被禁用，等待未来的优化
    /*
    const issues = [];

    // 🔧 修复：只检查明确的异常情况，避免在产品切换时误判
    
    // 检查基本状态 - 只检查明显的不一致
    if (this.isPlaying && !this.currentHowl) {
      issues.push("状态不一致：isPlaying=true但currentHowl=null");
    }

    // 🔧 移除过于严格的索引越界检查，因为在产品切换时这是正常的
    // 产品切换时，currentIndex 可能会被设置为 queue.length，这时的越界是正常的
    
    // 🔧 保留定时器状态检查，但只在明确异常时才认为有问题
    if (this.isPlaying && !this.progressTimer && !this.seamlessProgressTimer && this.currentHowl && this.currentHowl.playing()) {
      issues.push("状态不一致：正在播放但没有进度监控定时器");
    }

    if (issues.length > 0) {
      this.logMobile(`播放器健康检查发现问题: ${issues.join("; ")}`, "error");
      return false;
    }

    return true;
    */
  }

  // 🔧 新增：修复播放器状态不一致问题
  fixPlayerState(): boolean {
    this.logMobile("尝试修复播放器状态不一致问题", "warn");

    let fixed = false;

    // 修复状态不一致
    if (this.isPlaying && !this.currentHowl) {
      this.logMobile("修复: 停止播放状态，因为没有有效的音频", "warn");
      this.isPlaying = false;
      this.stopProgressTimer();
      fixed = true;
    }

    // 🔧 移除索引越界修复，因为在产品切换时这可能是正常的
    // 产品切换时，currentIndex 被设置为 queue.length 是正常行为，不应该被强制修复

    // 修复定时器状态 - 只有在明确需要时才修复
    if (
      this.isPlaying &&
      !this.progressTimer &&
      !this.seamlessProgressTimer &&
      this.currentHowl &&
      this.currentHowl.playing()
    ) {
      this.logMobile("修复: 重新启动进度监控", "warn");
      this.startProgressTimer();
      fixed = true;
    }

    // 🔧 保留恢复音频引用的逻辑，但更加谨慎
    if (
      this.currentIndex < this.playlist.length &&
      !this.currentHowl &&
      this.isPlaying
    ) {
      const currentItem = this.playlist[this.currentIndex];
      if (currentItem && currentItem.howl) {
        this.logMobile("修复: 恢复当前音频引用", "warn");
        this.currentHowl = currentItem.howl;
        fixed = true;
      }
    }

    if (fixed) {
      this.logMobile("播放器状态修复完成", "info");
    } else {
      this.logMobile("未发现需要修复的状态问题", "info");
    }

    return fixed;
  }

  // 获取播放器状态
  getStatus() {
    return {
      isPlaying: this.isPlaying,
      currentIndex: this.currentIndex,
      totalItems: this.playlist.length,
      currentItem: this.playlist[this.currentIndex] || null,
      currentTime: this.currentHowl
        ? (this.currentHowl.seek() as number) || 0
        : 0,
      duration: this.playlist[this.currentIndex]?.duration || 0,
      volume: this.instanceVolume,
      muted: this.instanceMuted,
      roomId: this.roomId,
      waitingForProgressRestore: this.waitingForProgressRestore,
      // 🔧 新增：添加健康状态检查
      isHealthy: this.isPlayerHealthy()
    };
  }

  // 🔧 新增：若播放器空闲且有可播条目，尝试启动
  public startIfIdle() {
    try {
      if (!this.isPlaying && this.currentIndex < this.playlist.length) {
        this.playNext("refill");
      }
    } catch {}
  }

  // 清理资源
  destroy() {
    this.logMobile(`销毁房间${this.roomId}的Howler播放器`, "warn");

    // 🔧 关键修复：添加严格的状态清理，防止频繁切换产品时出现状态混乱
    this.forceCleanupAllStates();

    this.stop();

    // 🎵 清理无缝播放定时器
    if (this.seamlessProgressTimer) {
      clearInterval(this.seamlessProgressTimer);
      this.seamlessProgressTimer = undefined;
    }

    // 🔧 关键修复：不调用全局Howler.unload()，避免影响其他房间
    // 只清理当前实例的音频
    this.playlist.forEach(item => {
      if (item.howl) {
        try {
          item.howl.unload();
        } catch (error) {
          this.logMobile(`清理音频失败: ${error}`, "warn");
        }
      }
    });

    this.playlist = [];
    this.currentHowl = null;

    // 移除可见性监听
    try {
      if (this.visibilityHandler) {
        document.removeEventListener(
          "visibilitychange",
          this.visibilityHandler
        );
        this.visibilityHandler = undefined;
      }
    } catch {}
  }

  // 🔧 新增：强制清理所有状态，防止频繁切换产品时的状态混乱
  private forceCleanupAllStates() {
    this.logMobile("强制清理所有状态，防止状态混乱", "warn");

    // 停止所有播放
    this.isPlaying = false;

    // 重置所有无缝播放状态
    this.hasTriggeredCompletion = false;
    this.isOverlapPlaying = false;

    // 清理所有定时器
    if (this.seamlessProgressTimer) {
      clearInterval(this.seamlessProgressTimer);
      this.seamlessProgressTimer = undefined;
    }

    // 解绑事件驱动的进度监听
    this.stopProgressTimer();

    // 重置恢复标志
    this.waitingForProgressRestore = false;

    // 停止当前播放的音频
    if (this.currentHowl) {
      try {
        this.currentHowl.stop();
      } catch (error) {
        this.logMobile(`停止当前音频失败: ${error}`, "warn");
      }
    }

    // 停止并清空所有播放列表中的音频
    this.playlist.forEach(item => {
      if (item.howl) {
        try {
          item.howl.stop();
          item.howl.unload(); // 🔧 新增：卸载音频资源
        } catch (error) {
          this.logMobile(`停止播放列表音频失败: ${error}`, "warn");
        }
      }
    });

    // 🔧 新增：彻底清空播放列表和相关状态
    this.playlist = [];
    this.currentIndex = 0;
    this.currentHowl = null;

    this.logMobile("强制清理完成，所有状态已重置", "info");
  }

  // 🍎 Safari专用：尝试无缝拼接新音频到当前播放
  private async trySeamlessAppend(newItem: HowlerAudioItem): Promise<boolean> {
    if (!this.currentHowl || !this.isPlaying) {
      return false;
    }

    const currentProgress = this.currentHowl.seek() as number;
    const currentDuration = this.currentHowl.duration();
    const remainingTime = currentDuration - currentProgress;

    // 🍎 如果当前音频剩余时间很短（<2秒），尝试拼接
    if (remainingTime < 3.5 && remainingTime > 0.1) {
      this.logMobile(
        `🍎 Safari尝试拼接: 剩余${remainingTime.toFixed(2)}s`,
        "info"
      );

      try {
        // 预加载新音频
        const useHtml5 = this.isMobileDevice()
          ? true
          : !this.shouldUseWebAudio();
        const nextHowl = new Howl({
          src: [newItem.audioUrl],
          html5: useHtml5,
          preload: true,
          format: ["mp3", "wav", "aac"],
          autoplay: false,
          loop: false,
          onload: () => {
            newItem.duration = nextHowl.duration();
            this.logMobile(
              `🍎 Safari拼接音频加载完成: ${newItem.duration?.toFixed(2)}s`,
              "info"
            );
          }
        });

        newItem.howl = nextHowl;
        nextHowl.volume(this.instanceMuted ? 0 : this.instanceVolume);

        // 添加到播放列表但不立即播放
        this.playlist.push(newItem);

        // 🍎 改为事件驱动：监听当前节点的 timeupdate，当接近尾声时触发切换（无定时器）
        try {
          const node = (this.currentHowl as any)?._sounds?.[0]?._node as
            | HTMLAudioElement
            | undefined;
          if (node && !(node as any).__appendBound) {
            const epsilon = Math.max(
              0.05,
              Math.min(0.25, (newItem.duration || 1) * 0.1)
            ); // 动态阈值（秒）
            const onTime = () => {
              const cur = Number(node.currentTime || 0);
              const dur = Number(this.currentHowl?.duration() || 0);
              if (dur > 0 && dur - cur <= epsilon) {
                node.removeEventListener("timeupdate", onTime);
                (node as any).__appendBound = undefined;
                this.performSeamlessSwitch(nextHowl, newItem);
              }
            };
            node.addEventListener("timeupdate", onTime);
            (node as any).__appendBound = { onTime };
            this.logMobile(
              `🍎 Safari拼接计划事件化: epsilon=${epsilon.toFixed(2)}s`,
              "info"
            );
          }
        } catch {}
        return true;
      } catch (error) {
        this.logMobile(`🍎 Safari拼接失败: ${error}`, "error");
        return false;
      }
    }

    return false;
  }

  // 🍎 Safari专用：执行无缝切换（不依赖定时器轮询）
  private performSeamlessSwitch(nextHowl: Howl, nextItem: HowlerAudioItem) {
    this.logMobile(`🍎 Safari执行无缝切换`, "warn");

    // 立即停止当前音频的监控
    if (this.seamlessProgressTimer) {
      clearInterval(this.seamlessProgressTimer);
      this.seamlessProgressTimer = undefined;
    }

    // 清理当前音频的拼接监听（如有）
    try {
      const node = (this.currentHowl as any)?._sounds?.[0]?._node as
        | HTMLAudioElement
        | undefined;
      const bound = node && (node as any).__appendBound;
      if (node && bound?.onTime) {
        node.removeEventListener("timeupdate", bound.onTime);
        (node as any).__appendBound = undefined;
      }
    } catch {}

    const oldHowl = this.currentHowl;
    this.currentHowl = nextHowl;

    // 🍎 立即开始播放新音频（音量设为0）
    nextHowl.volume(0);
    nextHowl.play();

    // 🍎 交叉淡入淡出
    if (oldHowl) {
      const currentVolume = oldHowl.volume();
      const targetVolume = this.instanceMuted ? 0 : this.instanceVolume;

      try {
        // 老音频快速淡出（无需定时 stop，自然结束）
        oldHowl.fade(currentVolume, 0, 120);
        // 新音频快速淡入
        nextHowl.fade(0, targetVolume, 120);
      } catch {
        // 回退处理
        try {
          oldHowl.stop();
        } catch {}
        nextHowl.volume(targetVolume);
      }
    }

    // 推进索引（因为新添加的项目在列表末尾）
    this.currentIndex = this.playlist.length - 1;

    // 🍎 设置新音频的监控和回调
    this.setupSeamlessOverlapPlay(nextHowl, nextItem);
    this.startProgressTimer();

    // 🍎 Safari专用：设置Media Session
    if (this.isSafari() || this.isIOSLike()) {
      this.setupSafariMediaSession(nextItem);
    }

    this.logMobile(
      `🍎 Safari无缝切换完成，索引推进到: ${this.currentIndex}`,
      "info"
    );
  }
}

// 已经在类和接口定义时直接导出了，不需要重复导出
